import { OpenFeatureProvider } from "@openfeature/react-sdk";
import { InMemoryProvider, OpenFeature } from "@openfeature/web-sdk";
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { useParams } from "react-router";

type FeatureFlagContextValue = {
	loading: boolean;
	error: string | null;
};

const FeatureFlagContext = createContext<FeatureFlagContextValue>({
	loading: true,
	error: null,
});

export function FeatureFlagProvider({ children }: { children: ReactNode }) {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;

		async function init() {
			try {
				const res = await fetch(`/api/tenant/${workspaceSlug}/flags`, {
					credentials: "include",
				});
				if (!res.ok) {
					throw new Error(`Failed to fetch flags: ${res.status}`);
				}
				const data = (await res.json()) as { flags: Record<string, boolean> };

				const flagConfig: Record<
					string,
					{
						variants: { on: boolean; off: boolean };
						defaultVariant: string;
						disabled: boolean;
					}
				> = {};
				for (const [key, value] of Object.entries(data.flags)) {
					flagConfig[key] = {
						variants: { on: true, off: false },
						defaultVariant: value ? "on" : "off",
						disabled: false,
					};
				}

				const provider = new InMemoryProvider(flagConfig);
				await OpenFeature.setProviderAndWait(provider);

				if (!cancelled) {
					setLoading(false);
				}
			} catch (err) {
				if (!cancelled) {
					setError(
						err instanceof Error ? err.message : "Failed to load feature flags",
					);
					setLoading(false);
				}
			}
		}

		init();
		return () => {
			cancelled = true;
		};
	}, [workspaceSlug]);

	return (
		<FeatureFlagContext.Provider value={{ loading, error }}>
			<OpenFeatureProvider>{children}</OpenFeatureProvider>
		</FeatureFlagContext.Provider>
	);
}

export function useFeatureFlagsReady() {
	return useContext(FeatureFlagContext);
}
